import { ellipsizeText } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';

import Serviced from 'webapp/models-custom/serviced';
import type DChartComparePoint from 'webapp/d-chart/point/compare-point';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

const MAX_TOOLTIP_TITLE_CHARS = 400;

export default class DChartTooltipBase extends Serviced {
	point: DChartPrimaryPoint;

	tooltipTypeClass = 'base';

	mainPointBeforeCompares = true;
	showNoValuePoints = true;
	showCompareChangeStr = true;

	constructor(primaryPoint: DChartPrimaryPoint) {
		super();
		this.point = primaryPoint;
	}

	get colorIndexOrClass() {
		return this.point.colorIndexOrClass;
	}

	get comparePoints() {
		return this.point.comparePoints;
	}

	get timeLabel() {
		return this.point.timeLabelLong || this.point.timeLabel;
	}

	getTooltipHtmlForPoint(comparePoint?: DChartComparePoint): string {
		const selectedPoint = comparePoint || this;

		// TOP
		const topHtml = this.getTooltipTop();

		const comparePoints = this.comparePoints;

		// MAIN point
		let mainPointHtml = '';
		{
			const isSelected = selectedPoint === this && comparePoints.length > 0,
				pointHtml = this.getPrimaryInnerTooltip();
			if (pointHtml) {
				mainPointHtml = this.wrapTooltipPoint(pointHtml, false, isSelected);
			}
		}

		// COMPARE points
		let comparePointsHtml = '';
		{
			comparePoints.forEach((point) => {
				const isSelected = selectedPoint === point,
					pointHtml = this.getCompareInnerTooltip(point);
				if (pointHtml) {
					comparePointsHtml += this.wrapTooltipPoint(pointHtml, true, isSelected);
				}
			});
		}

		let output = topHtml;
		if (this.mainPointBeforeCompares) {
			output += mainPointHtml + comparePointsHtml;
		} else {
			output += comparePointsHtml + mainPointHtml;
		}

		output += this.getTooltipBottom();

		return output;
	}

	protected getTooltipTop() {
		if (this.point.titleLong) {
			// cap very long titles (e.g. multi-line dimension names)
			const title = ellipsizeText(this.point.titleLong, MAX_TOOLTIP_TITLE_CHARS);
			return `<div class="top color-${this.colorIndexOrClass}"><div class="title color-fg">${sanitize(title)}</div></div>`;
		} else {
			return '';
		}
	}

	protected wrapTooltipPoint(content: string, isSecondary = false, isCurrent = false) {
		const classes: string[] = [
			'point-ct',
			`point-ct--${isSecondary ? 'secondary' : 'primary'}`,
		];
		if (isCurrent) {
			classes.push('point-ct--current');
		}
		return `<div class="${classes.join(' ')}">${content}</div>`;
	}

	// --- primary specifics ---

	protected getPrimaryInnerTooltip() {
		if (!this.showNoValuePoints && !this.point.hasValue) {
			return '';
		}

		let pointHtml = this.formatPrimaryTop();
		pointHtml += `<span class="value">${this.point.valueStr}</span>`;
		pointHtml += this.formatPrimaryBottom();

		return pointHtml;
	}

	protected formatPrimaryTop() {
		let pointHtml = '';
		const timeLabel = this.timeLabel;
		if (timeLabel) {
			pointHtml += `<div class="date">${timeLabel}</div>`;
		}
		return pointHtml;
	}

	protected getTooltipBottom() {
		if (this.point.isActionable) {
			return '<div class="bottom">Click for more actions</div>';
		} else {
			return '';
		}
	}

	protected formatPrimaryBottom(): string {
		return '';
	}

	// --- compare specifics ---

	protected getColorIndexOrClassForCompare(comparePoint: DChartComparePoint) {
		if (
			comparePoint.isForecastOptimistic ||
			comparePoint.isForecastPessimistic ||
			comparePoint.isForecast
		) {
			return `forecast-${comparePoint.compareType}`;
		} else {
			return this.colorIndexOrClass;
		}
	}

	protected getCompareInnerTooltip(comparePoint: DChartComparePoint) {
		if (!this.showNoValuePoints && !comparePoint.hasValue) {
			return '';
		}

		let pointHtml = '';

		pointHtml += this.getCompareTop(comparePoint);

		if (this.showCompareChangeStr) {
			pointHtml += this.formatCompareChangeStr(comparePoint);
		}

		if (comparePoint.isAnomaly) {
			pointHtml += this.addAnomalyInfoToCompareHtml(comparePoint);
		} else {
			pointHtml += `<span class="value">${comparePoint.valueStr}</span>`;

			pointHtml += this.getTimeLabelHtmlForCompare(comparePoint);
		}

		return pointHtml;
	}

	protected getCompareTop(_comparePoint: DChartComparePoint) {
		return '';
	}

	protected formatCompareChangeStr(comparePoint: DChartComparePoint) {
		if (comparePoint.changeStr) {
			let changeHtml = `<span class="change">${comparePoint.changeStr}</span>`;
			const compareText = comparePoint.compareText;
			if (compareText) {
				changeHtml += `<span class="text">${compareText}</span>`;
			}
			return `<span class="compare compare--${comparePoint.indicatorClass}">${changeHtml}</span>`;
		}
		return '';
	}

	private getTimeLabelHtmlForCompare(comparePoint: DChartComparePoint) {
		let timeLabel = '';
		if (!comparePoint.isGoal && !comparePoint.isOneOfForecasts) {
			timeLabel = `on ${comparePoint.timeLabel}`;
		}
		if (timeLabel) {
			return `<span class="date">${timeLabel}</span>`;
		} else {
			return '';
		}
	}

	private addAnomalyInfoToCompareHtml(comparePoint: DChartComparePoint) {
		const primaryPointValue = comparePoint.primaryPoint.value;
		const anomalyValue = comparePoint.value;
		const anomalyValueStr = comparePoint.valueStr;
		const anomalyData = comparePoint.anomalyData;

		let pointHtml = '';

		if (anomalyValue && anomalyData && primaryPointValue) {
			const headerHtml = `<div><h6>Anomaly detected</h6><span style="color: var(--${anomalyData.color}); background-color: var(--${anomalyData.bgColor})">${anomalyValueStr}</span></div>`;

			const anomalyText = `<p>${anomalyData.text}</p>`;

			pointHtml += `<div class="anomaly">${headerHtml}${anomalyText}</div>`;
		}

		return pointHtml;
	}
}
